import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { add, startOfDay, sub } from "date-fns";
import { Redirect, useLocation } from "react-router-dom";
import {
  AddIcon,
  Button,
  ButtonColor,
  CollapseAllIcon,
  ExpandAllIcon,
  Fade,
  IconButton,
  PartsIcon,
  Text,
  TextVariant,
  TopBar,
  TopBarPosition,
} from "../components/ui";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { Layout } from "../components/Layout";
import { PullToRefreshArea, PullToRefreshBar, usePullToRefresh } from "../components/PullToRefresh";
import { PartsFilterTabs } from "../components/Parts/PartsFilterTabs";
import { StoreGroup } from "../components/Parts/StoreGroup";
import { JobGroup } from "../components/Parts/JobGroup";
import { PartsGroupingTabs } from "../components/Parts/PartsGroupingTabs";
import { ConfirmPickupDialog } from "../components/Parts/ConfirmPickupDialog";
import { ReportIssueDialog } from "../components/Parts/ReportIssueDialog";
import { AddPartOrderDialog } from "../components/Parts/AddPartOrderDialog";
import { PartsEmptyState } from "../components/Parts/PartsEmptyState";
import {
  groupByJob,
  groupByStore,
  jobWindowOf,
  orderMatchesFilter,
  PartsFilter,
  PartsGrouping,
} from "../components/Parts/partsStatus.utils";
import { GET_MY_PART_ORDERS } from "../graphql/queries/getMyPartOrders";
import { UPDATE_PART_ORDER_ITEM_STATUS } from "../graphql/mutations/updatePartOrderItemStatus";
import { UPDATE_PART_ORDER } from "../graphql/mutations/updatePartOrder";
import { CREATE_PART_ORDER } from "../graphql/mutations/createPartOrder";
import {
  CreatePartOrderItemWithoutPartOrderInput,
  PartOrder,
  PartOrderItemStatus,
  Query,
} from "../generated/nest-graphql";
import { useHasPartsTab } from "../hooks/useUiv";
import { usePartOrderJobOptions } from "../hooks/usePartOrderJobOptions";
import { useRefetchOnForeground } from "../hooks/useRefetchOnForeground";
import { useWithdrawnJobFilter } from "../hooks/useWithdrawnJobFilter";
import { useDispatch } from "../contexts/snackbar-context";
import { showErrorAlert, showSuccessAlert } from "../actions";

/**
 * Inventory/Parts — every part order behind today's jobs, grouped by store.
 *
 * "Today" is the browser's local day, matching the schedule and every other date in
 * this app. The technician's stored timezone is deliberately not consulted: nothing
 * else here does, and disagreeing with the calendar they work from would be worse
 * than the edge case it would fix.
 */
const PartsPage: React.FC = () => {
  const hasPartsTab = useHasPartsTab();
  const dispatch = useDispatch();
  const reduceMotion = usePrefersReducedMotion();

  // State, not a one-off read, so a tab left open overnight moves to the new day when it comes back.
  const [today, setToday] = useState(() => startOfDay(new Date()));
  const { startRange, endRange, partsStartRange } = useMemo(() => {
    return {
      startRange: today,
      endRange: add(today, { days: 1 }),
      // Only the parts query reaches back a day, for the Picked up tab; the job picker
      // stays on today, since a new order should not be filed against yesterday's work.
      partsStartRange: sub(today, { days: 1 }),
    };
  }, [today]);

  // "View Parts" on the schedule list links here as ?groupBy=job&job=<id>.
  const location = useLocation();
  const { requestedGrouping, requestedJobId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      requestedGrouping: params.get("groupBy") === "job" ? ("JOB" as PartsGrouping) : null,
      requestedJobId: params.get("job"),
    };
  }, [location.search]);

  const [filter, setFilter] = useState<PartsFilter>("ALL");
  const [grouping, setGrouping] = useState<PartsGrouping>(requestedGrouping ?? "STORE");
  const [pickupOrder, setPickupOrder] = useState<PartOrder | null>(null);
  const [issueOrder, setIssueOrder] = useState<PartOrder | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Tracks what's closed, not what's open, so a store arriving on refetch isn't hidden.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const { data, loading, refetch } = useQuery<Query>(GET_MY_PART_ORDERS, {
    variables: { startRange: partsStartRange, endRange },
    fetchPolicy: "cache-and-network",
    skip: !hasPartsTab,
  });

  const { jobOptions, refetch: refetchJobOptions } = usePartOrderJobOptions({
    startRange,
    endRange,
    skip: !hasPartsTab,
  });

  // Failures are silent: the cached list stays and pull-to-refresh still works.
  useRefetchOnForeground(() => {
    if (!hasPartsTab) return;
    const now = startOfDay(new Date());
    // New variables fetch on their own; refetching here would ask for yesterday again.
    if (now.getTime() !== today.getTime()) {
      setToday(now);
      return;
    }
    refetch().catch(() => undefined);
    refetchJobOptions().catch(() => undefined);
  });

  // A later link to a different job has to win over whatever is on screen now.
  useEffect(() => {
    if (requestedGrouping) setGrouping(requestedGrouping);
  }, [requestedGrouping]);

  /**
   * Which job arrived from a "View Parts" link. State rather than a straight read of
   * the query param, which never changes: the selection has to survive until the
   * technician moves on, then clear — acting on anything, or opening a different job,
   * means they have moved on. Interacting with the selected job itself does not.
   */
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(requestedJobId);

  useEffect(() => {
    setHighlightedJobId(requestedJobId);
  }, [requestedJobId]);

  const pullState = usePullToRefresh(() => Promise.all([refetch(), refetchJobOptions()]));

  const [updateItemStatus] = useMutation(UPDATE_PART_ORDER_ITEM_STATUS);
  const [updatePartOrder] = useMutation(UPDATE_PART_ORDER);
  const [createPartOrder] = useMutation(CREATE_PART_ORDER);

  const orders = useMemo(() => (data?.getMyPartOrders ?? []).filter(Boolean) as PartOrder[], [data]);

  const showPickedUp = useCallback(() => setFilter("COLLECTED"), []);
  useWithdrawnJobFilter({ orders, loading, jobId: requestedJobId, onWithdrawn: showPickedUp });

  // No appointment window counts as today — hiding it would lose real work.
  const isFromToday = useCallback(
    (order: PartOrder) => {
      const { start } = jobWindowOf(order);
      return !start || start >= startRange;
    },
    [startRange]
  );

  // Only "Picked up" sees yesterday; the others stay scoped to today.
  const counts = useMemo(() => {
    const todays = orders.filter(isFromToday);
    return {
      ALL: todays.filter((order) => orderMatchesFilter(order, "ALL")).length,
      AWAITING: todays.filter((order) => orderMatchesFilter(order, "AWAITING")).length,
      COLLECTED: orders.filter((order) => orderMatchesFilter(order, "COLLECTED")).length,
    };
  }, [orders, isFromToday]);

  const visibleOrders = useMemo(() => {
    const scoped = filter === "COLLECTED" ? orders : orders.filter(isFromToday);
    return scoped.filter((order) => orderMatchesFilter(order, filter));
  }, [orders, filter, isFromToday]);
  const storeGroups = useMemo(() => groupByStore(visibleOrders), [visibleOrders]);
  const jobGroups = useMemo(() => groupByJob(visibleOrders), [visibleOrders]);
  // Both groupings expose `key`, so collapse state and the expand/collapse-all control
  // work the same either way. Store and job ids never collide, so the two views keep
  // their own collapse state rather than fighting over one set of keys.
  // Mapped per branch, not on a StoreGrouping[] | JobGrouping[] union: calling .map on
  // a union of array types is not callable, since the two signatures don't unify.
  const groupKeys = useMemo(
    () => (grouping === "JOB" ? jobGroups.map((group) => group.key) : storeGroups.map((group) => group.key)),
    [grouping, jobGroups, storeGroups]
  );

  // One call per item: a partial failure is acceptable here and self-corrects on refetch.
  const confirmPickup = async (itemIds: string[]) => {
    setSubmitting(true);
    try {
      await Promise.all(
        itemIds.map((id) => updateItemStatus({ variables: { id, status: PartOrderItemStatus.Collected } }))
      );
      await refetch();
      showSuccessAlert(dispatch, itemIds.length === 1 ? "Part marked as picked up." : "Parts marked as picked up.");
      setPickupOrder(null);
    } catch (error) {
      showErrorAlert(dispatch, "That did not save. Check your signal and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reportIssue = async (issue: string, notes: string) => {
    if (!issueOrder) return;
    setSubmitting(true);
    try {
      // No `items` key — sending one would rewire this order's links to the job's
      // service lines, and an empty array would delete its parts outright.
      await updatePartOrder({
        variables: {
          id: issueOrder.id,
          updatePartOrderInput: {
            partOrderingIssue: issue,
            partOrderingIssueNotes: notes || null,
          },
        },
      });
      await refetch();
      showSuccessAlert(dispatch, "Reported. HQ can see this now.");
      setIssueOrder(null);
    } catch (error) {
      showErrorAlert(dispatch, "That did not send. Check your signal and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const createOrder = async (input: {
    job: string;
    orderNumber: string | null;
    store: string | null;
    notes: string | null;
    items: CreatePartOrderItemWithoutPartOrderInput[];
  }): Promise<string | null> => {
    setSubmitting(true);
    try {
      const result = await createPartOrder({
        variables: {
          // `items` is always present, empty included: the backend reconciles job
          // service lines against this array.
          createPartOrderInput: input,
        },
      });
      await refetch();
      showSuccessAlert(dispatch, "Part order saved.");
      return result.data?.createPartOrder?.id ?? null;
    } catch (error) {
      showErrorAlert(dispatch, "The order could not be saved. Check your signal and try again.");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!requestedJobId || grouping !== "JOB") return;
    if (scrolledFor.current === requestedJobId) return;
    const target = document.getElementById(`job-group-${requestedJobId}`);
    if (!target) return;
    scrolledFor.current = requestedJobId;
    // Centred, not "start": the header here is four stacked pieces (toolbar, filter
    // tabs, grouping tabs, progress bar) and top-aligning puts the card behind them.
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    // Depends on jobGroups so it runs once the group exists, not before the data lands.
  }, [requestedJobId, grouping, jobGroups, reduceMotion]);

  if (!hasPartsTab) return <Redirect to="/" />;

  const hasJobsToday = jobOptions.length > 0;
  const allCollapsed = groupKeys.length > 0 && groupKeys.every((key) => collapsed[key]);

  const toggleAll = () =>
    setCollapsed(
      allCollapsed ? {} : groupKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>)
    );

  return (
    <Layout
      appBar={
        // One sticky block, so the tabs don't need the app bar's height as an offset.
        <div className="qa-sticky-offset sticky top-0 z-20 bg-gray-900 border-b border-gray-600">
          <TopBar position={TopBarPosition.STATIC} contentClassName="flex justify-between">
            <span className="flex items-center gap-1">
              <PartsIcon className="mr-1" />
              <Text variant={TextVariant.H6}>My Parts Orders</Text>
              <IconButton
                color={ButtonColor.INHERIT}
                onClick={toggleAll}
                disabled={groupKeys.length === 0}
                aria-label={allCollapsed ? "Expand all groups" : "Collapse all groups"}
                title={allCollapsed ? "Expand all" : "Collapse all"}
              >
                {allCollapsed ? <ExpandAllIcon /> : <CollapseAllIcon />}
              </IconButton>
            </span>
            <Button
              color={ButtonColor.INHERIT}
              startIcon={<AddIcon />}
              onClick={() => {
                setHighlightedJobId(null);
                setAddOpen(true);
              }}
              disabled={!hasJobsToday}
            >
              Add New PO
            </Button>
          </TopBar>
          <PartsFilterTabs value={filter} onChange={setFilter} counts={counts} />
          <PartsGroupingTabs value={grouping} onChange={setGrouping} />
          <PullToRefreshBar state={pullState} loading={loading} />
        </div>
      }
    >
      {/* The draggable area sits outside the Fade: MUI's transition clones its child
          and hands it a ref, which a plain function component cannot receive. */}
      <PullToRefreshArea state={pullState}>
        {/* Keyed on filter+grouping so switching either remounts and replays the fade. */}
        <Fade visible appear key={`${filter}-${grouping}`} durationMs={reduceMotion ? 0 : 220}>
          <div className="p-3">
            {groupKeys.length === 0 && !loading && (
              <PartsEmptyState hasJobsToday={hasJobsToday} hasAnyOrders={orders.length > 0} filter={filter} />
            )}
            {grouping === "STORE"
              ? storeGroups.map((group) => (
                  <StoreGroup
                    key={group.key}
                    group={group}
                    filter={filter}
                    open={!collapsed[group.key]}
                    onToggle={() => setCollapsed((current) => ({ ...current, [group.key]: !current[group.key] }))}
                    onConfirmPickup={setPickupOrder}
                    onReportIssue={setIssueOrder}
                  />
                ))
              : jobGroups.map((group) => (
                  <JobGroup
                    key={group.key}
                    group={group}
                    filter={filter}
                    highlighted={!!highlightedJobId && group.key === highlightedJobId}
                    open={!collapsed[group.key]}
                    onToggle={() => {
                      // Opening a different job means they've moved on; opening this one doesn't.
                      if (group.key !== highlightedJobId) setHighlightedJobId(null);
                      setCollapsed((current) => ({ ...current, [group.key]: !current[group.key] }));
                    }}
                    onConfirmPickup={(order) => {
                      setHighlightedJobId(null);
                      setPickupOrder(order);
                    }}
                    onReportIssue={(order) => {
                      setHighlightedJobId(null);
                      setIssueOrder(order);
                    }}
                  />
                ))}
          </div>
        </Fade>
      </PullToRefreshArea>

      <ConfirmPickupDialog
        order={pickupOrder}
        open={!!pickupOrder}
        submitting={submitting}
        onClose={() => setPickupOrder(null)}
        onConfirm={confirmPickup}
      />

      <ReportIssueDialog
        order={issueOrder}
        open={!!issueOrder}
        submitting={submitting}
        onClose={() => setIssueOrder(null)}
        onSubmit={reportIssue}
      />

      <AddPartOrderDialog
        open={addOpen}
        jobs={jobOptions}
        submitting={submitting}
        onClose={() => setAddOpen(false)}
        onCreate={createOrder}
      />
    </Layout>
  );
};

export default PartsPage;
